import React from "react";
import * as styles from "./Panel.module.scss";
import classNames from "classnames";
import PropTypes from "prop-types";
import Button from "../Button";
import Tooltip from "../Tooltip";
import BadgeWarning from "@app/assets/images/badge_warning.svg";

export const PanelCells = ({ cells, className }) => (
	<div className={classNames(styles["header-info"], className)}>
		{cells?.map(({ label, value }, index) => (
			<div key={index}>
				<div className={styles["label"]}>{label}</div>
				<div className={styles["value"]}>{value}</div>
			</div>
		))}
	</div>
);

const Panel = ({ children, header, hasBg, buttons, className, warning, highlightClass }) => {
	const panelClassName = classNames({
		[styles["panel"]]: true,
		[styles["has-background"]]: hasBg,
		[styles["header-colored"]]: header?.colored,
		[styles["header-no-separator"]]: header?.noSeparator,
		[styles["has-warning"]]: warning,
		[className]: !!className,
		[highlightClass]: !!highlightClass,
	});

	return (
		<div className={panelClassName}>
			{highlightClass && <div className={classNames(styles["highlight"], highlightClass)} />}
			{warning && <img className={styles["warning"]} src={BadgeWarning} alt="important" />}
			{header && (
				<div className={styles["header"]}>
					<h2 className={styles["headline"]}>
						{header?.label}
						{header?.tooltip && <Tooltip content={<span>{header?.tooltip}</span>} />}
					</h2>
					{header?.cells?.length && <PanelCells cells={header.cells} />}
					{header?.actions?.length && (
						<div className={styles["header-actions"]}>
							{header?.actions?.map(({ type, iconBefore, callback, isSubmit, ...props }, index) => (
								<Button
									key={index}
									type={type}
									iconBefore={iconBefore}
									isSubmit={isSubmit}
									callback={callback}
									{...props}
								/>
							))}
						</div>
					)}
					{header?.children}
				</div>
			)}
			<div className={styles["content"]}>
				{children}
				{buttons && (
					<div className={styles["buttons"]}>
						{buttons?.map(({ label, type, onClick, isSubmit, ...props }, index) => (
							<Button
								key={index}
								label={label}
								type={type}
								isSubmit={isSubmit}
								callback={onClick}
								{...props}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

Panel.propTypes = {
	children: PropTypes.node,
	header: PropTypes.shape({
		colored: PropTypes.bool,
		label: PropTypes.node.isRequired,
		cells: PropTypes.arrayOf(
			PropTypes.shape({
				label: PropTypes.string.isRequired,
				value: PropTypes.string.isRequired,
			}),
		),
		actions: PropTypes.array,
		tooltip: PropTypes.string,
	}),
	hasBg: PropTypes.bool,
	buttons: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			type: PropTypes.string.isRequired,
			onClick: PropTypes.func.isRequired,
			isSubmit: PropTypes.bool,
		}),
	),
	className: PropTypes.string,
	highlightClass: PropTypes.string,
};

export default Panel;
